/**
 * Calendar days in a named IANA time zone. The browser works in its own local time; the server needs the user's zone
 * explicitly, because the hosted edition runs in UTC and serves people everywhere (settings.timeZone).
 */

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatters.set(timeZone, f);
  }
  return f;
}

/** The zone this process runs in: TZ on Node, UTC in Workers. */
export function processTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function isValidTimeZone(timeZone: string): boolean {
  try {
    formatter(timeZone);
    return true;
  } catch {
    return false;
  }
}

interface Fields {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function fieldsIn(instant: number, timeZone: string): Fields {
  const out: Record<string, number> = {};
  for (const part of formatter(timeZone).formatToParts(new Date(instant))) {
    if (part.type !== 'literal') out[part.type] = Number(part.value);
  }
  return out as unknown as Fields;
}

/** How far the zone's wall clock is ahead of UTC at `instant`, in ms. */
function offsetAt(instant: number, timeZone: string): number {
  const f = fieldsIn(instant, timeZone);
  const wall = Date.UTC(f.year, f.month - 1, f.day, f.hour, f.minute, f.second);
  return wall - (instant - (((instant % 1000) + 1000) % 1000));
}

const pad = (n: number) => String(n).padStart(2, '0');

/** 'YYYY-MM-DD' of the calendar day `date` falls on in `timeZone`. */
export function dayKey(date: Date, timeZone: string): string {
  const f = fieldsIn(date.getTime(), timeZone);
  return `${f.year}-${pad(f.month)}-${pad(f.day)}`;
}

/** Midnight that starts the calendar day `offsetDays` after the one containing `date`, in `timeZone` (DST-safe). */
export function startOfDay(date: Date, timeZone: string, offsetDays = 0): Date {
  const f = fieldsIn(date.getTime(), timeZone);
  return wallMidnightIn(Date.UTC(f.year, f.month - 1, f.day + offsetDays), timeZone);
}

/** Midnight that starts the day key `key` ('YYYY-MM-DD'), or the one `offsetDays` after it, in `timeZone`. */
export function startOfDayKey(key: string, timeZone: string, offsetDays = 0): Date {
  const [y, m, d] = key.split('-').map(Number);
  return wallMidnightIn(Date.UTC(y!, m! - 1, d! + offsetDays), timeZone);
}

/** The instant the zone's wall clock reads `wallMidnight` (a UTC timestamp standing for that wall time). */
function wallMidnightIn(wallMidnight: number, timeZone: string): Date {
  let instant = wallMidnight - offsetAt(wallMidnight, timeZone);
  // The offset at the guess can differ from the one at the real midnight when a DST change falls in between.
  const corrected = wallMidnight - offsetAt(instant, timeZone);
  if (corrected !== instant) instant = corrected;
  return new Date(instant);
}

const dayNumber = (key: string) => {
  const [y, m, d] = key.split('-').map(Number);
  return Math.round(Date.UTC(y!, m! - 1, d!) / 86400000);
};

/** Whole calendar days from day key `a` to day key `b` ('YYYY-MM-DD'). */
export function daysBetweenKeys(a: string, b: string): number {
  return dayNumber(b) - dayNumber(a);
}
